import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import DashboardSidebar from "./DashboardSidebar";
import { SearchHeader } from "@/components/dashboard/SearchHeader";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Use React Query for better state management and automatic retries
  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session;
    },
    retry: 1, // Reduce retries to prevent excessive loading
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["user", session?.user?.id],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      return user;
    },
    enabled: !!session?.user?.id,
    retry: 1, // Reduce retries to prevent excessive loading
    staleTime: 1000 * 60 * 5,
    // Add a short refetch interval to ensure we have the latest user data
    refetchInterval: false,
  });

  // Listen for auth changes
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        if (event === "SIGNED_OUT" || !session) {
          // Invalidate queries and redirect
          queryClient.invalidateQueries({ queryKey: ["session"] });
          queryClient.invalidateQueries({ queryKey: ["user"] });
          queryClient.invalidateQueries({ queryKey: ["profile"] });
          navigate("/signin");
        } else if (event === "SIGNED_IN") {
          // Refresh queries when signed in
          queryClient.invalidateQueries({ queryKey: ["session"] });
          queryClient.invalidateQueries({ queryKey: ["user"] });
          queryClient.invalidateQueries({ queryKey: ["profile"] });
        }
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [navigate, queryClient]);

  // Redirect if no session
  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate("/signin");
    }
  }, [session, sessionLoading, navigate]);

  // Show loading state
  if (sessionLoading || userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if no user
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <p className="text-sm text-destructive">
            Session expired. Please sign in again.
          </p>
          <button
            onClick={() => navigate("/signin")}
            className="text-primary hover:underline"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {isMobile ? (
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetContent side="left" className="p-0 w-full sm:w-[300px]">
            <DashboardSidebar
              isOpen={true}
              onToggle={() => setIsMobileMenuOpen(false)}
              isMobile={true}
            />
          </SheetContent>
        </Sheet>
      ) : (
        <DashboardSidebar
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          isMobile={false}
        />
      )}

      <div
        className={cn(
          "flex-1 flex flex-col transition-all duration-300 ease-in-out",
          !isMobile && isSidebarOpen ? "md:ml-64" : "md:ml-20"
        )}
      >
        <SearchHeader
          onMenuClick={() => setIsMobileMenuOpen(true)}
          isMobile={isMobile}
        />
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
